import { useState } from "react";
import { Loader2, Link2, AlertCircle, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";

const LABELS = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  cookies: "Cookie Policy",
};

/**
 * Surfaces when the auto-discovery couldn't find one or more legal docs.
 * Accepts URLs on the same apex domain and triggers a re-scan in place —
 * same scan_id, same /report/{domain} URL, score gets overwritten.
 *
 * Hidden when:
 *  - missing_docs is empty
 *  - the scan is Pro-unlocked (locked once buyer paid — see backend guard)
 */
export default function ManualUrlsBanner({ report, onUpdate }) {
  const missing = report.missing_docs || [];
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState({ privacy: "", terms: "", cookies: "" });
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);

  if (missing.length === 0 || report.pro_unlocked) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const k of missing) {
      const v = (urls[k] || "").trim();
      if (v) payload[`${k}_url`] = v;
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Paste at least one URL");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/scan/${report.id}/rescan-with-urls`, payload);
      toast.success("Re-scanning your site — refreshing the report in a moment.");
      setPolling(true);
      // Poll the scan; flip status from 'rescanning' / 'analyzing' back to 'done'
      const started = Date.now();
      const tick = async () => {
        if (Date.now() - started > 90_000) {
          setPolling(false);
          setSubmitting(false);
          toast.error("Re-scan took too long — refresh manually");
          return;
        }
        try {
          const r = await apiClient.get(`/scan/${report.id}`);
          if (r.data?.status === "done" && r.data?.rescan_count > (report.rescan_count || 0)) {
            setPolling(false);
            setSubmitting(false);
            await onUpdate?.();
            const delta = (r.data.trust_score ?? 0) - (report.trust_score ?? 0);
            if (delta > 0) {
              toast.success(`Score lifted by +${delta} points → ${r.data.trust_score}/100`);
            } else {
              toast.message(`Re-scan complete — score: ${r.data.trust_score}/100`);
            }
            return;
          }
        } catch {
          /* ignore poll errors */
        }
        setTimeout(tick, 2000);
      };
      tick();
    } catch (err) {
      setSubmitting(false);
      toast.error(err?.response?.data?.detail || "Could not start re-scan");
    }
  };

  // Cookies are excluded if the privacy policy already covered them
  const showCookies = missing.includes("cookies");

  return (
    <section
      className="border-b border-black bg-[#FEF7EC]"
      data-testid="manual-urls-banner"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#92400E] mb-2">
            / Heads up
          </div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05]">
            We couldn't auto-find{" "}
            <span className="bg-[#F59E0B] text-black px-1.5">
              {missing.map((m) => LABELS[m]).join(", ")}
            </span>{" "}
            on {report.domain}.
          </h2>
          <p className="text-[#475569] mt-4 leading-relaxed">
            Reasons vary — different language paths, custom CMS routes, content
            gated behind a login, or a sitemap that hides them. <strong className="text-black">If your site
            actually has these documents, paste the direct URLs below and we'll
            re-scan in place.</strong> Your TrustScore will almost always go up once
            we can read them.
          </p>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#475569] mt-3">
            Same-domain URLs only · in-place update · ~30s
          </p>
        </div>

        <div className="lg:col-span-7">
          {!open ? (
            <button
              onClick={() => setOpen(true)}
              className="w-full bg-[#0A0A0A] text-white border border-[#0A0A0A] px-5 py-4 inline-flex items-center justify-center gap-2 hover:bg-[#1A1A1A]"
              data-testid="manual-urls-open"
            >
              <Sparkles className="w-4 h-4" />
              Paste the missing URL{missing.length > 1 ? "s" : ""}
            </button>
          ) : (
            <form onSubmit={onSubmit} className="bg-white border border-[#0A0A0A] p-5 space-y-4">
              {missing.includes("privacy") && (
                <UrlField
                  label="Privacy Policy URL"
                  testid="manual-privacy-url"
                  placeholder={`https://${report.domain}/privacy-policy`}
                  value={urls.privacy}
                  onChange={(v) => setUrls((s) => ({ ...s, privacy: v }))}
                />
              )}
              {missing.includes("terms") && (
                <UrlField
                  label="Terms of Service URL"
                  testid="manual-terms-url"
                  placeholder={`https://${report.domain}/terms`}
                  value={urls.terms}
                  onChange={(v) => setUrls((s) => ({ ...s, terms: v }))}
                />
              )}
              {showCookies && (
                <UrlField
                  label="Cookie Policy URL"
                  optional
                  testid="manual-cookies-url"
                  placeholder={`https://${report.domain}/cookies`}
                  value={urls.cookies}
                  onChange={(v) => setUrls((s) => ({ ...s, cookies: v }))}
                />
              )}
              {report.privacy_mentions_cookies && (
                <div className="text-[11px] font-mono text-[#475569] flex items-start gap-1.5 bg-[#F8F9FA] p-2 border border-[#E2E8F0]">
                  <CheckCircle2 className="w-3 h-3 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Your privacy policy already covers cookies — no separate URL needed.
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="submit"
                  disabled={submitting || polling}
                  className="pl-btn-primary px-5 py-3 border border-black inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="manual-urls-submit"
                >
                  {submitting || polling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Re-scanning…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Re-scan with these URLs
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={submitting || polling}
                  className="px-4 py-3 border border-[#0A0A0A] hover:bg-[#F1F5F9] text-sm font-mono uppercase tracking-widest disabled:opacity-60"
                  data-testid="manual-urls-cancel"
                >
                  Cancel
                </button>
              </div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
                URLs must be on {report.domain}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function UrlField({ label, value, onChange, placeholder, optional, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] flex items-center gap-1.5">
        <Link2 className="w-3 h-3" />
        {label}
        {optional && <span className="text-[#94A3B8] normal-case tracking-normal">(optional)</span>}
      </label>
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 border border-[#0A0A0A] px-3 py-2.5 text-sm outline-none focus:bg-[#F8F9FA] font-mono"
        data-testid={testid}
      />
    </div>
  );
}
